import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Flex } from 'reflexbox';

import { useErrorBoundary, useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { LabelType } from '@treams/treams-network';
import {
	ActionArchiveListItem,
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	SettingsSection,
	Spinner,
	Select as TreamsSelect,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import GoalCreateCompetency from '../../packages/kakapo-ui-kit/components/goals/create/GoalCreateCompetency';

function GoalTemplateEditor(props: { goalTemplate?: graphql.GoalTemplate }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const history = useHistory();
	const { localize } = useLocalize();
	const hasGoalLabels = useFeature(graphql.FeatureName.GoalLabels);

	const goalLabels = graphql.usePaginatedLabelsQuery({
		variables: {
			isArchived: false,
			type: LabelType.Goal,
		},
	});

	const jobTitles = graphql.useJobTitlesQuery({
		variables: {
			first: 1000,
			isArchived: false,
		},
	});

	const goalLabelsOptions = goalLabels.data?.paginatedLabels.edges.map((edge) => ({
		value: edge.node.id,
		title: localize(edge.node.title).text,
		description: edge.node.description ? localize(edge.node.description).text : '',
	}));

	const jobTitleOptions = jobTitles.data?.jobTitles.edges.map(({ node }) => ({
		value: node.id,
		label: localize(node.title).text,
	}));

	const [error, attempt, reset] = useErrorBoundary();

	const [create, { loading: createLoading }] = graphql.useCreateGoalTemplateMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateGoalTemplateMutation();

	const loading = createLoading || updateLoading;

	const formik = useFormik({
		initialValues: {
			title: props.goalTemplate?.title ?? [],
			description: props.goalTemplate?.description ?? [],
			competency: props.goalTemplate?.competency?.competency,
			label: props.goalTemplate?.label?.id,
			jobTitleIDs: props.goalTemplate?.jobTitles.map((jobTitle) => jobTitle.id) ?? [],
			competencyLevelInput: props.goalTemplate?.competency
				? {
						competency: props.goalTemplate?.competency?.competencyLevelIndex,
					}
				: undefined,
		},
		onSubmit: () => onUpdate(),
	});

	const onUpdate = attempt(async () => {
		reset();

		const input = {
			title: formik.values.title.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			description: formik.values.description?.map((string) => ({
				languageID: string.languageID,
				text: string.text,
			})),
			labelID: formik.values.label,
			competency:
				formik.values.competency && formik.values.competencyLevelInput
					? {
							competencyID: formik.values.competency.id,
							competencyLevelIndex: formik.values.competencyLevelInput.competency,
						}
					: undefined,
			relativeDueDate: undefined,
			jobTitleIDs: formik.values.jobTitleIDs,
		};

		if (props.goalTemplate) {
			await update({
				variables: {
					input: {
						id: props.goalTemplate.id,
						...input,
					},
				},
			});
		} else {
			const { data } = await create({
				variables: { input },
			});

			if (data) {
				history.push(`/settings/goals/templates/${data.createGoalTemplate.goalTemplate.id}`);
			}
		}

		addToast(t('common.settings.goals.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const valid = formik.values.title.some((translation) => translation.text.length > 0);

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			<Heading mb={0}>
				{t(`common.settings.goals.${props.goalTemplate ? 'edit' : 'create'}.title`)}
			</Heading>
			<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
				<GraphQLErrorMessage error={error} />
				<FormikProvider value={formik}>
					<SettingsSection first title={t('common.settings.goals.edit.details')}>
						<Form>
							<DetailsTableForm
								formValues={[
									{
										label: t('common.settings.goals.edit.name'),
										name: 'title',
										type: DetailsTableFormValueTypes.Multilingual,
									},
									{
										label: t('common.settings.goals.edit.description'),
										name: 'description',
										type: DetailsTableFormValueTypes.MultilingualQuill,
									},
									{
										label: t('common.settings.goals.edit.competency'),
										name: 'competency',
										type: DetailsTableFormValueTypes.Custom,
										control: <GoalCreateCompetency standalone />,
									},
									...(hasGoalLabels
										? [
												{
													label: t('common.settings.goals.edit.label'),
													name: 'label',
													type: DetailsTableFormValueTypes.Custom,
													control: (
														<TreamsSelect
															placeholder={t('goals.list.label.placeholder')}
															options={goalLabelsOptions}
															selectedValue={formik.values.label}
															onChange={(value) => {
																formik.setFieldValue('label', value);
															}}
														/>
													),
												},
											]
										: []),
									{
										label: t('common.settings.goals.edit.jobTitles.label'),
										name: 'jobTitleIDs',
										type: DetailsTableFormValueTypes.MultiSelect,
										options: jobTitleOptions,
										description: t('common.settings.goals.edit.jobTitles.hint'),
									},
								]}
							/>
						</Form>
					</SettingsSection>
				</FormikProvider>

				<Flex justifyContent="flex-end">
					<Button
						title={t('common.save')}
						onClick={() => formik.submitForm()}
						loading={loading}
						disabled={!valid}
					/>
				</Flex>

				{props.goalTemplate && (
					<ActionArchiveListItem
						archivableItem={props.goalTemplate}
						archivableItemName={t('common.settings.goals.itemName')}
						archivableItemKey="goalTemplate"
						useArchive={graphql.useArchiveGoalTemplateMutation}
						useRestore={graphql.useRestoreGoalTemplateMutation}
					/>
				)}
			</FlexGap>
		</FlexGap>
	);
}

function GoalTemplateEditorID(props: { id: string }) {
	const { data, error, loading } = graphql.useGoalTemplateQuery({
		variables: { id: props.id },
	});

	if (loading) {
		return (
			<Flex my={4} justifyContent="center">
				<Spinner />
			</Flex>
		);
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <GoalTemplateEditor goalTemplate={data!.goalTemplate as graphql.GoalTemplate} />;
	}
}

export { GoalTemplateEditor as GoalTemplateCreator };

export default GoalTemplateEditorID;
